import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { mondayOf, weekDays } from './autoSchedule'
import DayOffRequestModal from './DayOffRequestModal'
import FindReplacementModal from './FindReplacementModal'
import ReplacementOfferInbox from './ReplacementOfferInbox'
import { notifyWorker, getOwnerIds } from '../../lib/notifyWorker'

const DAY_LABELS = ['MON','TUE','WED','THU','FRI','SAT']
const STATUS_COLORS = {
  scheduled: { bg: 'rgba(74,140,106,0.18)', border: 'rgba(74,140,106,0.5)', fg: '#6fbf8e' },
  day_off: { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)', fg: 'rgba(255,255,255,0.5)' },
  called_off: { bg: 'rgba(201,162,74,0.15)', border: 'rgba(201,162,74,0.4)', fg: '#c9a24a' },
  no_call_no_show: { bg: 'rgba(224,92,92,0.15)', border: 'rgba(224,92,92,0.4)', fg: '#e05c5c' },
}
const STATUS_LABELS = { scheduled: 'SCHEDULED', day_off: 'DAY OFF', called_off: 'CALLED OFF', no_call_no_show: 'NCNS' }

function fmtShort(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return (d.getMonth() + 1) + '/' + d.getDate()
}

export default function MyScheduleView() {
  const { profile, user } = useAuth()
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()))
  const [mySchedule, setMySchedule] = useState([])
  const [jobs, setJobs] = useState([])
  const [fullGrid, setFullGrid] = useState([]) // optional full team view
  const [allWorkers, setAllWorkers] = useState([])
  const [showRequest, setShowRequest] = useState(false)
  const [showFindReplacement, setShowFindReplacement] = useState(null) // a day_off_request row
  const [showFullCrew, setShowFullCrew] = useState(false)
  const [mySeason, setMySeason] = useState({ days_worked: 0, total_hours: 0 })
  const [blockedDates, setBlockedDates] = useState([])
  const [newBlock, setNewBlock] = useState({ date: '', reason: '' })
  const [availability, setAvailability] = useState([])
  const [savingAvail, setSavingAvail] = useState(false)

  const days = useMemo(() => weekDays(weekStart), [weekStart])
  const weekStartStr = weekStart.toISOString().slice(0, 10)
  const weekEndStr = days[days.length - 1]

  const load = useCallback(async () => {
    if (!user) return
    const { data: mine } = await supabase.from('worker_schedule')
      .select('*')
      .eq('user_id', user.id)
      .gte('schedule_date', weekStartStr)
      .lte('schedule_date', weekEndStr)
    setMySchedule(mine || [])

    // Jobs referenced
    const jobIds = Array.from(new Set((mine || []).map((r) => r.job_id).filter(Boolean)))
    if (jobIds.length > 0) {
      const { data: jobRows } = await supabase.from('jobs')
        .select('id, title, client_name, site_address, business_id, start_date, end_date, status')
        .in('id', jobIds)
      setJobs(jobRows || [])
    } else setJobs([])

    // Season totals from worker_schedule (days worked this season)
    if (profile?.season_start) {
      const start = profile.season_start
      const end = profile.season_end || new Date().toISOString().slice(0, 10)
      const { data: seasonRows } = await supabase.from('worker_schedule')
        .select('schedule_date, status')
        .eq('user_id', user.id)
        .gte('schedule_date', start)
        .lte('schedule_date', end)
        .eq('status', 'scheduled')
      const daysWorked = (seasonRows || []).length
      const { data: hourRows } = await supabase.from('job_time_entries')
        .select('hours_worked, clock_in')
        .eq('user_id', user.id)
        .gte('clock_in', start + 'T00:00:00')
        .lte('clock_in', end + 'T23:59:59')
      const totalHours = (hourRows || []).reduce((s, r) => s + (Number(r.hours_worked) || 0), 0)
      setMySeason({ days_worked: daysWorked, total_hours: totalHours })
    }
    // Load blocked dates
    var { data: bdRows } = await supabase.from('worker_blocked_dates').select('*').eq('user_id', user.id).order('blocked_date')
    setBlockedDates(bdRows || [])
    // Load availability
    var { data: avRows } = await supabase.from('worker_availability').select('*').eq('user_id', user.id).order('day_of_week')
    setAvailability(avRows || [])
  }, [user, profile, weekStartStr, weekEndStr])

  const loadFullCrew = useCallback(async () => {
    if (!showFullCrew) return
    const { data: wRows } = await supabase.from('profiles')
      .select('id, full_name, role, seniority_rank')
      .neq('role', 'store_partner')
      .eq('is_active', true)
      .eq('availability_status', 'available')
    setAllWorkers(wRows || [])
    const { data: schedRows } = await supabase.from('worker_schedule')
      .select('user_id, schedule_date, status, job_id')
      .gte('schedule_date', weekStartStr)
      .lte('schedule_date', weekEndStr)
    setFullGrid(schedRows || [])
  }, [showFullCrew, weekStartStr, weekEndStr])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadFullCrew() }, [loadFullCrew])

  const shiftWeek = (delta) => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + 7 * delta)
    setWeekStart(mondayOf(d))
  }

  const cellFor = (dateStr) => mySchedule.find((r) => r.schedule_date === dateStr)
  const jobOf = (jobId) => jobs.find((j) => j.id === jobId)

  const handleSubmitRequest = async ({ date, reason }) => {
    const { data, error } = await supabase.from('day_off_requests').insert({
      user_id: user.id,
      request_date: date,
      reason: reason || null,
      status: 'pending',
    }).select().single()
    if (error) {
      alert('Error: ' + error.message)
      return
    }
    // Notify owner + crew_leader
    const ownerIds = await getOwnerIds()
    const { data: myJob } = await supabase.from('worker_schedule')
      .select('job_id')
      .eq('user_id', user.id)
      .eq('schedule_date', date)
      .maybeSingle()
    let crewLeaderId = null
    if (myJob?.job_id) {
      const { data: jobRow } = await supabase.from('jobs').select('crew_leader_id').eq('id', myJob.job_id).maybeSingle()
      crewLeaderId = jobRow?.crew_leader_id
    }
    const notifyIds = Array.from(new Set([...ownerIds, crewLeaderId].filter(Boolean)))
    await Promise.all(notifyIds.map((uid) => notifyWorker({
      userId: uid,
      type: 'day_off_requested',
      title: 'Day off request',
      message: (profile?.full_name || 'A worker') + ' — ' + fmtShort(date),
      link: '/schedule?request=' + data.id,
      relatedId: data.id,
    })))

    setShowRequest(false)
    setShowFindReplacement(data)
    await load()
  }

  return (
    <div style={styles.wrap}>
      <ReplacementOfferInbox onChange={load} />

      <div style={styles.weekNav}>
        <button onClick={() => shiftWeek(-1)} style={styles.navBtn}>←</button>
        <div style={styles.weekLabel}>
          {fmtShort(days[0])} – {fmtShort(days[5])}
        </div>
        <button onClick={() => shiftWeek(1)} style={styles.navBtn}>→</button>
        <button onClick={() => setWeekStart(mondayOf(new Date()))} style={styles.todayBtn}>THIS WEEK</button>
        <button onClick={() => setShowRequest(true)} style={styles.requestBtn}>REQUEST DAY OFF</button>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>THIS WEEK</div>
        <div style={styles.dayGrid}>
          {days.map((d, i) => {
            const cell = cellFor(d)
            const status = cell?.status || 'day_off'
            const col = STATUS_COLORS[status]
            const job = cell?.job_id ? jobOf(cell.job_id) : null
            return (
              <div key={d} style={{ ...styles.dayCard, borderColor: col.border, backgroundColor: col.bg }}>
                <div style={styles.dayCardLabel}>{DAY_LABELS[i]} · {fmtShort(d)}</div>
                <div style={{ ...styles.dayCardStatus, color: col.fg }}>{STATUS_LABELS[status]}</div>
                {job && (
                  <div style={styles.dayCardJob}>
                    <div style={styles.jobTitle}>{job.title || job.client_name}</div>
                    {job.site_address && <div style={styles.jobAddr}>{job.site_address}</div>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>MY SEASON</div>
        <div style={styles.statGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{mySeason.days_worked}</div>
            <div style={styles.statLabel}>DAYS WORKED</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{mySeason.total_hours.toFixed(1)}</div>
            <div style={styles.statLabel}>HOURS</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {profile?.season_start ? (() => {
                const weeks = Math.max(1, Math.ceil((new Date() - new Date(profile.season_start)) / (86400000 * 7)))
                return (mySeason.days_worked / weeks).toFixed(1)
              })() : '—'}
            </div>
            <div style={styles.statLabel}>AVG DAYS / WEEK</div>
          </div>
        </div>
      </div>

      {/* MY AVAILABILITY */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>MY SEASON DATES</div>
        <div style={{display:'flex',gap:'12px',marginBottom:'12px'}}>
          <div style={{flex:1}}><div style={{fontSize:'10px',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'4px'}}>START DATE</div><input type="date" value={profile?.season_start||''} onChange={async function(e){await supabase.from('profiles').update({season_start:e.target.value,availability_status:'available'}).eq('id',user.id)}} style={{width:'100%',padding:'10px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'14px',outline:'none',boxSizing:'border-box'}} /></div>
          <div style={{flex:1}}><div style={{fontSize:'10px',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'4px'}}>END DATE</div><input type="date" value={profile?.season_end||''} onChange={async function(e){await supabase.from('profiles').update({season_end:e.target.value}).eq('id',user.id)}} style={{width:'100%',padding:'10px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'14px',outline:'none',boxSizing:'border-box'}} /></div>
        </div>
      </div>

      {/* BLOCKED DATES */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>BLOCKED DATES</div>
        <div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)',marginBottom:'10px'}}>Request specific dates off. Owner must approve.</div>
        <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
          <input type="date" value={newBlock.date} onChange={function(e){setNewBlock(Object.assign({},newBlock,{date:e.target.value}))}} style={{flex:1,padding:'10px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'14px',outline:'none'}} />
          <input value={newBlock.reason} onChange={function(e){setNewBlock(Object.assign({},newBlock,{reason:e.target.value}))}} style={{flex:2,padding:'10px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'14px',outline:'none'}} placeholder="Reason (optional)" />
          <button onClick={async function(){if(!newBlock.date)return;await supabase.from('worker_blocked_dates').insert({user_id:user.id,blocked_date:newBlock.date,reason:newBlock.reason||null,status:'pending'});setNewBlock({date:'',reason:''});load()}} style={{padding:'10px 16px',backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'1px',cursor:'pointer'}}>ADD</button>
        </div>
        {blockedDates.map(function(bd){return <div key={bd.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'3px',marginBottom:'4px'}}>
          <div><span style={{color:'#fff',fontSize:'14px'}}>{bd.blocked_date}</span>{bd.reason && <span style={{color:'rgba(255,255,255,0.3)',fontSize:'12px',marginLeft:'10px'}}>{bd.reason}</span>}</div>
          <span style={{fontSize:'10px',fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600,letterSpacing:'1px',color:bd.status==='approved'?'#4a8c6a':bd.status==='denied'?'#e05c5c':'#b48c3c'}}>{(bd.status||'PENDING').toUpperCase()}</span>
        </div>})}
      </div>

      {/* WEEKLY AVAILABILITY */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>WEEKLY AVAILABILITY</div>
        <div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)',marginBottom:'10px'}}>Set your recurring weekly schedule. Students: set after-school times.</div>
        <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
          {[1,2,3,4,5,6].map(function(dow){
            var dayName = ['','MON','TUE','WED','THU','FRI','SAT'][dow]
            var row = availability.find(function(a){return a.day_of_week===dow})
            var avail = row ? row.available : true
            var afterTime = row?.available_after || ''
            return <div key={dow} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'3px'}}>
              <span style={{width:'40px',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:600,letterSpacing:'1px',color:'rgba(255,255,255,0.5)'}}>{dayName}</span>
              <select value={avail?(afterTime?'after':'yes'):'no'} onChange={async function(e){
                setSavingAvail(true)
                var val = e.target.value
                var update = { user_id: user.id, day_of_week: dow, available: val !== 'no', available_after: val === 'after' ? '15:00' : null }
                await supabase.from('worker_availability').upsert(update, { onConflict: 'user_id,day_of_week' })
                load(); setSavingAvail(false)
              }} style={{padding:'6px 10px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'13px',outline:'none'}} disabled={savingAvail}>
                <option value="yes">Available All Day</option>
                <option value="after">After School</option>
                <option value="no">Not Available</option>
              </select>
              {afterTime && <input type="time" value={afterTime} onChange={async function(e){setSavingAvail(true);await supabase.from('worker_availability').update({available_after:e.target.value}).eq('user_id',user.id).eq('day_of_week',dow);load();setSavingAvail(false)}} style={{padding:'6px 8px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#b48c3c',fontSize:'13px',outline:'none'}} />}
              <span style={{fontSize:'11px',color:avail?'#4a8c6a':'#e05c5c'}}>{avail?(afterTime?'After '+afterTime:'ALL DAY'):'OFF'}</span>
            </div>
          })}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>
          <button onClick={() => setShowFullCrew(!showFullCrew)} style={styles.toggleLink}>
            {showFullCrew ? '▾' : '▸'} FULL CREW SCHEDULE
          </button>
        </div>
        {showFullCrew && (
          <div style={styles.crewGrid}>
            <div style={styles.crewHeadRow}>
              <div style={styles.crewNameSlot}></div>
              {days.map((d, i) => (
                <div key={d} style={styles.crewDayHead}>
                  <div style={styles.crewDayLabel}>{DAY_LABELS[i]}</div>
                  <div style={styles.crewDayDate}>{fmtShort(d)}</div>
                </div>
              ))}
            </div>
            {allWorkers.sort((a,b) => (a.seniority_rank||999) - (b.seniority_rank||999)).map((w) => (
              <div key={w.id} style={styles.crewRow}>
                <div style={styles.crewName}>
                  <div style={styles.crewNameText}>{w.full_name}</div>
                  <div style={styles.crewNameRole}>{(w.role||'').replace('_',' ')}</div>
                </div>
                {days.map((d) => {
                  const row = fullGrid.find((r) => r.user_id === w.id && r.schedule_date === d)
                  const st = row?.status || 'day_off'
                  const col = STATUS_COLORS[st]
                  return (
                    <div key={d} style={{ ...styles.crewCell, backgroundColor: col.bg, borderColor: col.border, color: col.fg }}>
                      {st === 'scheduled' ? '●' : st === 'day_off' ? '○' : st === 'called_off' ? '!' : 'X'}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {showRequest && (
        <DayOffRequestModal
          onClose={() => setShowRequest(false)}
          onSubmit={handleSubmitRequest}
        />
      )}
      {showFindReplacement && (
        <FindReplacementModal
          request={showFindReplacement}
          onClose={() => setShowFindReplacement(null)}
        />
      )}
    </div>
  )
}

const styles = {
  wrap: { fontFamily: "'Barlow', sans-serif" },
  weekNav: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px' },
  navBtn: { width: '32px', height: '32px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', cursor: 'pointer' },
  weekLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', letterSpacing: '2px', color: '#b48c3c', minWidth: '120px', textAlign: 'center' },
  todayBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '6px 10px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
  requestBtn: { marginLeft: 'auto', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '10px 16px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  section: { marginBottom: '28px' },
  sectionLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  toggleLink: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', padding: 0 },
  dayGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' },
  dayCard: { padding: '12px', border: '1px solid', borderRadius: '4px', minHeight: '90px' },
  dayCardLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)' },
  dayCardStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', letterSpacing: '1.5px', fontWeight: 700, marginTop: '6px' },
  dayCardJob: { marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)' },
  jobTitle: { fontSize: '12px', color: '#ffffff', fontWeight: 500 },
  jobAddr: { fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
  statCard: { padding: '16px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', textAlign: 'center' },
  statValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '30px', fontWeight: 700, color: '#b48c3c' },
  statLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  crewGrid: { display: 'flex', flexDirection: 'column', gap: '4px' },
  crewHeadRow: { display: 'grid', gridTemplateColumns: '140px repeat(6, 1fr)', gap: '4px' },
  crewNameSlot: {},
  crewDayHead: { textAlign: 'center', padding: '6px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '3px' },
  crewDayLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.5)' },
  crewDayDate: { fontSize: '11px', color: 'rgba(255,255,255,0.7)' },
  crewRow: { display: 'grid', gridTemplateColumns: '140px repeat(6, 1fr)', gap: '4px' },
  crewName: { padding: '6px 10px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '3px' },
  crewNameText: { fontSize: '12px', color: '#ffffff' },
  crewNameRole: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' },
  crewCell: { padding: '6px', border: '1px solid', borderRadius: '3px', textAlign: 'center', fontSize: '14px' },
}
